import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Pagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AddRounded,
  DescriptionOutlined,
  FilterAltOffOutlined,
  FolderSharedOutlined,
  PersonOutline,
  SettingsOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingDocumentListItem,
  DocumentCraftingDocumentListResponse,
  DocumentCraftingUser,
} from "../../../Models/documentCrafting";
import { useDocumentCraftingDocuments } from "../../../Query/useDocumentCrafting";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
} from "../Common/DocumentCraftingUi";
import {
  documentCraftingPaths,
  useDocumentCraftingAccess,
} from "../Common/documentCraftingNavigation";
import DocumentCards from "./DocumentCards";
import DocumentFilters from "./DocumentFilters";
import DocumentTable from "./DocumentTable";
import {
  DOCUMENT_LIST_PAGE_SIZES,
  DocumentListParams,
  useDocumentListParams,
} from "./useDocumentListParams";

const NewDocumentButton: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  return (
    <Button
      variant="contained"
      startIcon={<AddRounded />}
      onClick={() => navigate(documentCraftingPaths.create())}
    >
      {t("nav.newDocument")}
    </Button>
  );
};

/** Which empty state: nothing at all, nothing in this scope, or nothing
 * matching the search and filters. */
const ListEmptyState: React.FC<{ params: DocumentListParams }> = ({
  params,
}) => {
  const { t } = useTranslation("documentCrafting");
  if (params.isFiltered) {
    return (
      <EmptyState
        icon={<FilterAltOffOutlined />}
        title={t("list.empty.noResultsTitle")}
        text={t("list.empty.noResultsText")}
        action={
          <Button variant="outlined" onClick={params.clearFilters}>
            {t("list.filters.clear")}
          </Button>
        }
      />
    );
  }
  if (params.state.scope === "mine") {
    return (
      <EmptyState
        icon={<PersonOutline />}
        title={t("list.empty.mineTitle")}
        text={t("list.empty.mineText")}
        action={<NewDocumentButton />}
      />
    );
  }
  if (params.state.scope === "shared") {
    return (
      <EmptyState
        icon={<FolderSharedOutlined />}
        title={t("list.empty.sharedTitle")}
        text={t("list.empty.sharedText")}
      />
    );
  }
  return (
    <EmptyState
      icon={<DescriptionOutlined />}
      title={t("list.empty.firstTitle")}
      text={t("list.empty.firstText")}
      action={<NewDocumentButton />}
    />
  );
};

const ListFooter: React.FC<{
  data: DocumentCraftingDocumentListResponse;
  params: DocumentListParams;
}> = ({ data, params }) => {
  const { t } = useTranslation("documentCrafting");
  const pageCount = Math.ceil(data.total / params.state.pageSize);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1.5,
        mt: 2,
      }}
    >
      <Typography sx={{ fontSize: 13, color: color.gray500 }}>
        {t("common.shownOf", {
          shown: data.documents.length,
          total: data.total,
        })}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <TextField
          select
          size="small"
          label={t("list.pageSize")}
          value={params.state.pageSize}
          onChange={(event) => params.setPageSize(Number(event.target.value))}
          sx={{ width: 104 }}
        >
          {DOCUMENT_LIST_PAGE_SIZES.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </TextField>
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            page={Math.min(params.state.page, pageCount)}
            onChange={(_, page) => params.setPage(page)}
            color="primary"
            size="small"
            siblingCount={0}
          />
        )}
      </Box>
    </Box>
  );
};

// People on the page, so the "Created by" filter can name a preset member.
const usersOf = (documents: DocumentCraftingDocumentListItem[] | undefined) => {
  const users: DocumentCraftingUser[] = [];
  (documents ?? []).forEach((item) => {
    [item.created_by, item.owner, item.latest_revision?.saved_by].forEach(
      (user) => {
        if (user && !users.some((known) => known.id === user.id)) {
          users.push(user);
        }
      },
    );
  });
  return users;
};

const DocumentListContent: React.FC = () => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const params = useDocumentListParams();
  const { state, query, setPage } = params;
  const documents = useDocumentCraftingDocuments(query);
  const data = documents.data;
  const items = data?.documents;
  const knownUsers = useMemo(() => usersOf(items), [items]);

  // A page past the end (e.g. after deletions, or an old link) goes to the
  // last page that has documents.
  const lastPage = data ? Math.max(1, Math.ceil(data.total / state.pageSize)) : 1;
  const pastTheEnd =
    !!data &&
    !documents.isPlaceholderData &&
    !data.documents.length &&
    data.total > 0 &&
    state.page > lastPage;
  useEffect(() => {
    if (pastTheEnd) setPage(lastPage);
  }, [pastTheEnd, lastPage, setPage]);

  return (
    <Panel>
      <DocumentFilters params={params} knownUsers={knownUsers} />
      <Box sx={{ height: 3, mb: 1 }}>
        {documents.isFetching && documents.isPlaceholderData && (
          <LinearProgress sx={{ height: 3 }} />
        )}
      </Box>
      {documents.isPending ? (
        <LoadingState />
      ) : documents.isError && !data ? (
        <ErrorState onRetry={() => documents.refetch()} />
      ) : data && items && items.length ? (
        <Box
          sx={{
            opacity: documents.isPlaceholderData ? 0.6 : 1,
            transition: "opacity 120ms",
          }}
        >
          {isNarrow ? (
            <DocumentCards documents={items} />
          ) : (
            <DocumentTable
              documents={items}
              sort={state.sort}
              order={state.order}
              onSort={params.toggleSort}
            />
          )}
          <ListFooter data={data} params={params} />
        </Box>
      ) : pastTheEnd ? (
        <LoadingState />
      ) : (
        <ListEmptyState params={params} />
      )}
    </Panel>
  );
};

/** The document overview: every document the user can see in the
 * workspace plus their personal documents, with search, filters, sort and
 * paging kept in the URL. Users without a workspace work with personal
 * documents only. */
const DocumentList: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const access = useDocumentCraftingAccess();
  const ready = !access.isLoading && !access.isError;

  return (
    <>
      <PageHeader
        title={t("module.name")}
        subtitle={t("module.description")}
        actions={
          ready && (
            <>
              <Button
                variant="outlined"
                startIcon={<SettingsOutlined />}
                onClick={() => navigate(documentCraftingPaths.settings)}
              >
                {t("nav.settings")}
              </Button>
              <NewDocumentButton />
            </>
          )
        }
      />
      {access.isLoading ? (
        <Panel>
          <LoadingState />
        </Panel>
      ) : access.isError ? (
        <Panel>
          <ErrorState onRetry={() => access.refetch()} />
        </Panel>
      ) : (
        <>
          {!access.hasWorkspace && (
            <Alert severity="info" icon={<PersonOutline />} sx={{ mb: 2 }}>
              {t("list.personalScopeNote")}
            </Alert>
          )}
          <DocumentListContent />
        </>
      )}
    </>
  );
};

export default DocumentList;
